import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { getOneByRemote as getDocumentation } from 'serviceWorker/db/documentations/documentations';
import { getAll as getIssues } from 'serviceWorker/db/issues/issues';
import { get as getLevels } from 'serviceWorker/db/levels/levels';
import {
  hasLegacyShape,
  LegacyRequestContext,
  QueuedRequest,
  rewriteLegacyQueuedRequest,
} from 'serviceWorker/db/versioning/legacyQueuedRequests';
import type { DocumentationEntity } from 'serviceWorker/repository/documentation/entity';
import type { VersionedDocumentation } from 'shared/domain/documentation/currentVersion';
import { MapVersion, resolveMap } from 'shared/domain/documentation/levelMap';
import type { LocalizedOnMap } from 'shared/domain/issue/issueLocation';
import { LogLevel } from 'shared/types/logger';

/** A stored documentation as the map rules read it: withdrawn flags as booleans, a missing current version as `null`. */
function toVersionedDocumentation(documentation: DocumentationEntity): VersionedDocumentation<MapVersion> {
  return {
    deleted: Boolean(documentation.deleted),
    currentVersionId: documentation.currentVersionId ?? null,
    versions: documentation.versions.map((version) => ({ ...version, deleted: Boolean(version.deleted) })),
  };
}

/**
 * The device's levels and issues as they are stored now (PT-7117 shape): a
 * location goes on the map version the level shows, by the same rule as the
 * location form (`resolveMap`).
 */
async function readCurrentContext(): Promise<LegacyRequestContext> {
  const [levels, issues] = await Promise.all([getLevels(), getIssues()]);
  const levelMaps = new Map<string, LocalizedOnMap>();
  await Promise.all(
    levels.map(async ({ _id: levelId, map }) => {
      const documentation = map && (await getDocumentation(map.documentationId));
      const { shown } = resolveMap(map, () => (documentation ? toVersionedDocumentation(documentation) : undefined));
      if (map && shown) {
        levelMaps.set(levelId, { documentationId: map.documentationId, versionId: shown.version._id });
      }
    })
  );
  const issueLevels = new Map(issues.map((issue) => [issue._id, issue.primaryData.level]));
  return { findLevelMap: (levelId) => levelMaps.get(levelId), findIssueLevel: (issueId) => issueLevels.get(issueId) };
}

/**
 * Rewrites a request an old worker queued after migration 52 in the shape the
 * backend refuses since PT-7117, on the map the level shows now; any other
 * request is sent as it is, without reading the database.
 */
export async function rewriteLegacyVersioningRequest<TRequest extends QueuedRequest>(request: TRequest): Promise<TRequest> {
  if (!hasLegacyShape(request)) {
    return request;
  }
  const rewrite = rewriteLegacyQueuedRequest(request, await readCurrentContext());
  if (rewrite.droppedLocationOfIssue) {
    swLog('Queued issue location dropped, its level has no map', LogLevel.INFO, null, {
      entityName: 'issues',
      query: { issueId: rewrite.droppedLocationOfIssue },
    });
  }
  return rewrite.request;
}
